import { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import AlertModal from '../../../components/AlertModal/AlertModal';
import { DynamicSelect } from '../../../components/Inputs/Inputs';
import Button from '../../../components/Button/Button';
import { ReactComponent as User } from '../../../assets/user.svg';
import { ReactComponent as Loader } from '../../../assets/loader.svg';
import './MyHandoff.scss';

const QUEUE_POLL_MS = 10000;
const THREAD_POLL_MS = 4000;

const STATUS_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'away', label: 'Ausente' },
  { value: 'offline', label: 'Offline' },
];

export default function MyHandoff() {
  const { axiosAuth, accessibleHotels = [] } = useContext(AuthContext);

  const [hotelId, setHotelId] = useState('');
  const [status, setStatus] = useState(null); // desconhecido até o operador escolher
  const [savingStatus, setSavingStatus] = useState(false);

  const [queue, setQueue] = useState([]);
  const [mine, setMine] = useState([]);
  const [notOperator, setNotOperator] = useState(false);
  const [claimingId, setClaimingId] = useState(null);

  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [thread, setThread] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [sessionState, setSessionState] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);

  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });
  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: '', message: '' });
  }, []);

  // auto-seleciona o hotel se só houver um acessível
  useEffect(() => {
    if (!hotelId && accessibleHotels.length === 1) {
      setHotelId(String(accessibleHotels[0].id));
    }
  }, [accessibleHotels, hotelId]);

  // ao trocar de hotel, limpa a conversa em curso
  useEffect(() => {
    setSelectedSessionId('');
    setThread([]);
    setCursor(0);
    setSessionState(null);
  }, [hotelId]);

  const hotelOptions = useMemo(
    () => accessibleHotels.map((h) => ({ value: String(h.id), label: h.name })),
    [accessibleHotels],
  );

  // fila (por reivindicar) + minhas conversas (assigned/active) — polling
  const fetchQueueAndMine = useCallback(async () => {
    if (!hotelId) return;
    try {
      const [{ data: queueData }, { data: mineData }] = await Promise.all([
        axiosAuth.get('/human-handoff/queue', { params: { hotel_id: Number(hotelId) } }),
        axiosAuth.get('/human-handoff/sessions/mine', { params: { hotel_id: Number(hotelId) } }),
      ]);
      setQueue(Array.isArray(queueData) ? queueData : []);
      setMine(Array.isArray(mineData) ? mineData : []);
      setNotOperator(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setNotOperator(true);
        setQueue([]);
        setMine([]);
      } else {
        openError('Erro ao carregar o atendimento', err?.response?.data?.detail || err.message);
      }
    }
  }, [axiosAuth, hotelId, openError]);

  useEffect(() => {
    fetchQueueAndMine();
    if (!hotelId) return undefined;
    const id = setInterval(fetchQueueAndMine, QUEUE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchQueueAndMine, hotelId]);

  // conversa selecionada — polling do fio de mensagens a partir do cursor
  const fetchThread = useCallback(async () => {
    if (!hotelId || !selectedSessionId) return;
    try {
      const { data } = await axiosAuth.get(
        `/human-handoff/sessions/${selectedSessionId}/thread`,
        { params: { hotel_id: Number(hotelId), after: cursor } },
      );
      setSessionState(data?.state ?? null);
      if (Array.isArray(data?.messages) && data.messages.length > 0) {
        setThread((prev) => [...prev, ...data.messages]);
        setCursor(data.cursor);
      }
    } catch (err) {
      openError('Erro ao atualizar a conversa', err?.response?.data?.detail || err.message);
    }
  }, [axiosAuth, hotelId, selectedSessionId, cursor, openError]);

  useEffect(() => {
    if (!selectedSessionId) return undefined;
    fetchThread();
    const id = setInterval(fetchThread, THREAD_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSessionId, cursor]);

  const handleSelectSession = useCallback((sessionId) => {
    setSelectedSessionId(String(sessionId));
    setThread([]);
    setCursor(0);
  }, []);

  const handleSetStatus = useCallback(
    async (value) => {
      if (!hotelId) return;
      setSavingStatus(true);
      try {
        await axiosAuth.put(
          '/human-handoff/operators/me/status',
          { status: value },
          { params: { hotel_id: Number(hotelId) } },
        );
        setStatus(value);
      } catch (err) {
        openError('Erro ao mudar de estado', err?.response?.data?.detail || err.message);
      } finally {
        setSavingStatus(false);
      }
    },
    [axiosAuth, hotelId, openError],
  );

  const handleClaim = useCallback(
    async (sessionId) => {
      setClaimingId(sessionId);
      try {
        await axiosAuth.post(
          `/human-handoff/sessions/${sessionId}/claim`,
          {},
          { params: { hotel_id: Number(hotelId) } },
        );
        await fetchQueueAndMine();
        handleSelectSession(sessionId);
      } catch (err) {
        openError('Não foi possível reivindicar', err?.response?.data?.detail || err.message);
      } finally {
        setClaimingId(null);
      }
    },
    [axiosAuth, hotelId, fetchQueueAndMine, handleSelectSession, openError],
  );

  const handleSend = useCallback(async () => {
    const content = draft.trim();
    if (!content || !selectedSessionId) return;
    setSending(true);
    try {
      const { data } = await axiosAuth.post(
        `/human-handoff/sessions/${selectedSessionId}/reply`,
        { content },
        { params: { hotel_id: Number(hotelId) } },
      );
      setThread((prev) => [...prev, data]);
      setCursor((prev) => Math.max(prev, data.id));
      setDraft('');
    } catch (err) {
      openError('Erro ao enviar mensagem', err?.response?.data?.detail || err.message);
    } finally {
      setSending(false);
    }
  }, [axiosAuth, draft, hotelId, selectedSessionId, openError]);

  const handleClose = useCallback(async () => {
    if (!selectedSessionId) return;
    setClosing(true);
    try {
      await axiosAuth.post(
        `/human-handoff/sessions/${selectedSessionId}/close`,
        {},
        { params: { hotel_id: Number(hotelId) } },
      );
      setSessionState('closed');
      await fetchQueueAndMine();
    } catch (err) {
      openError('Erro ao fechar a conversa', err?.response?.data?.detail || err.message);
    } finally {
      setClosing(false);
    }
  }, [axiosAuth, hotelId, selectedSessionId, fetchQueueAndMine, openError]);

  const isSessionOpen = sessionState === 'assigned' || sessionState === 'active';

  return (
    <div className="my-handoff-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <div className="title-section">
        <PageTitle title="Atendimento" />
        <div className="status-picker">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`status-btn ${opt.value} ${status === opt.value ? 'active' : ''}`}
              disabled={!hotelId || savingStatus}
              onClick={() => handleSetStatus(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {hotelOptions.length > 1 && (
        <div className="hotel-selector">
          <DynamicSelect
            label="Hotel"
            placeholder="Escolher um hotel"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            options={hotelOptions}
          />
        </div>
      )}

      {!hotelId ? (
        <div className="empty-page">
          <p>Escolhe um hotel para veres o atendimento.</p>
        </div>
      ) : notOperator ? (
        <div className="empty-page">
          <p>
            Não estás registado como operador ativo para este hotel. Fala com o
            administrador para te inscreverem no Atendimento Humano.
          </p>
        </div>
      ) : (
        <div className="container">
          <div className="left-side">
            <div className="list-block">
              <h3>Fila ({queue.length})</h3>
              <div className="items">
                {queue.length === 0 && <p className="empty-hint">Sem pedidos em espera.</p>}
                {queue.map((q) => (
                  <div className="queue-item" key={q.session_id}>
                    <div className="avatar">
                      <User />
                    </div>
                    <div className="content">
                      <p className="waiting">
                        À espera há {Math.floor(q.waiting_seconds / 60)}m
                        {q.waiting_seconds % 60}s
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => handleClaim(q.session_id)}
                      disabled={claimingId === q.session_id}
                    >
                      Atender
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="list-block">
              <h3>Minhas conversas ({mine.length})</h3>
              <div className="items">
                {mine.length === 0 && <p className="empty-hint">Nenhuma conversa ativa.</p>}
                {mine.map((s) => {
                  const isActive = String(s.id) === String(selectedSessionId);
                  return (
                    <button
                      className={`each-session ${isActive ? 'active' : ''}`}
                      key={s.id}
                      onClick={() => handleSelectSession(s.id)}
                    >
                      <div className="avatar">
                        <User />
                      </div>
                      <div className="content">
                        <p className="session-name">Conversa #{s.id}</p>
                        <span className={`state-badge ${s.state}`}>
                          {s.state === 'active' ? 'Ativa' : 'Atribuída'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="right-side">
            <div className="chat">
              <div className="chat-header">
                <div className="avatar-name">
                  <div className="avatar">
                    <User />
                  </div>
                  <div className="name">
                    <p>
                      {selectedSessionId
                        ? `Conversa #${selectedSessionId}`
                        : 'Nenhuma conversa selecionada'}
                    </p>
                  </div>
                  {selectedSessionId && isSessionOpen && (
                    <div className="close-action">
                      <Button type="button" variant="secondary" onClick={handleClose} disabled={closing}>
                        Fechar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="chat-body">
                {!selectedSessionId && (
                  <div className="empty-state">
                    <Loader />
                    <p>Escolhe uma conversa da fila ou das tuas conversas.</p>
                  </div>
                )}

                {selectedSessionId &&
                  thread.map((m, idx) => {
                    const cls = m.sender === 'operator' ? 'operator-message' : 'visitor-message';
                    const dateStr = m.created_at ? new Date(m.created_at).toLocaleString() : '';
                    return (
                      <div className={cls} key={m.id || idx}>
                        <div className="message">
                          <p>{m.content}</p>
                        </div>
                        <div className="date">
                          <span>{dateStr}</span>
                        </div>
                      </div>
                    );
                  })}

                {selectedSessionId && !isSessionOpen && sessionState && (
                  <div className="closed-hint">
                    <p>Esta conversa está fechada.</p>
                  </div>
                )}
              </div>
              {selectedSessionId && isSessionOpen && (
                <div className="chat-input">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Escreve uma resposta..."
                    disabled={sending}
                  />
                  <Button type="button" onClick={handleSend} disabled={sending || !draft.trim()}>
                    Enviar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
